import React from "react";
import { Globe, Phone, Copy, FileText, Video, Image as ImageIcon } from "lucide-react";
import { cn } from "../ui";

/**
 * TemplatePreview — a WhatsApp message template as the customer will receive it.
 *
 * ── WHY THE COLOURS DO NOT FOLLOW THE THEME ─────────────────────────────────
 * Every colour here comes from the FIXED `--ext-whatsapp-*` set, never the
 * theme-flipping `--wa-client-*` one and never `--primary` (TOKEN_CONTRACT §8).
 * The pane exists to show an admin what the message looks like in the
 * RECIPIENT's client, so following our dark mode or the institute's brand would
 * make it stop answering the only question it is for. Same call
 * `TemplateSendModal` made; `FlowPhonePreview` is the deliberate opposite,
 * because that one is a handset following the app.
 *
 * Presentational only — it takes the template's parts and renders them.
 */

const ICON_FOR_MEDIA = { video: Video, document: FileText, image: ImageIcon };
const ICON_FOR_BUTTON = { URL: Globe, PHONE_NUMBER: Phone, COPY_CODE: Copy };

/** One CTA row under the bubble. A picture of a button, not a button: WhatsApp
 *  draws these and nothing here can be pressed, so no role and no handler. */
function PreviewButton({ type, children, muted = false }) {
  const Icon = ICON_FOR_BUTTON[type];
  return (
    <div
      className={cn(
        "tw-flex tw-items-center tw-justify-center tw-gap-0.5 tw-border-0 tw-border-t",
        "tw-border-solid tw-border-[var(--ext-whatsapp-bubble-line)] tw-px-1.5 tw-py-2",
        "tw-text-center tw-font-medium",
        muted
          ? "tw-text-[0.72rem] tw-text-[var(--ext-whatsapp-muted)]"
          : "tw-text-[0.78rem] tw-text-[var(--ext-whatsapp-btn)]",
      )}
    >
      {Icon && <Icon size={13} className="tw-mr-1" aria-hidden="true" />}
      {children}
    </div>
  );
}

const TemplatePreview = ({ headerType, headerText, media, body, footer, buttons = [] }) => {
  const MediaIcon = ICON_FOR_MEDIA[headerType] || ICON_FOR_MEDIA.image;
  const isMedia = Object.hasOwn(ICON_FOR_MEDIA, headerType);
  const hasBody = Boolean(body?.trim());

  return (
    <div className="tw-min-h-[180px] tw-rounded-md tw-bg-[var(--ext-whatsapp-wallpaper)] tw-px-2.5 tw-py-3.5">
      {/* `0 8px 8px 8px` — a received bubble squares off the corner by its tail. */}
      <div className="tw-relative tw-max-w-[280px] tw-overflow-hidden tw-rounded-[0_8px_8px_8px] tw-bg-[var(--ext-whatsapp-bubble-in)] tw-pb-1 tw-shadow-[var(--ext-whatsapp-bubble-shadow)]">
        {headerType === "text" && headerText?.trim() && (
          <div className="tw-px-2.5 tw-pt-2 tw-text-[0.82rem] tw-font-semibold tw-text-[var(--ext-whatsapp-ink)]">
            {headerText}
          </div>
        )}

        {isMedia && (media?.previewUrl ? (
          <img
            src={media.previewUrl}
            alt=""
            className="tw-block tw-max-h-[160px] tw-w-full tw-rounded-t-[4px] tw-object-cover"
          />
        ) : (
          /* Stands in for the media slot so the bubble keeps the shape the real
             message will have, sample uploaded or not. */
          <div className="tw-flex tw-h-[120px] tw-flex-col tw-items-center tw-justify-center tw-gap-1.5 tw-rounded-t-[4px] tw-bg-[var(--ext-whatsapp-panel)] tw-text-[0.72rem] tw-capitalize tw-text-[var(--ext-whatsapp-muted)]">
            <MediaIcon size={22} aria-hidden="true" />
            <span>{media?.handle ? "Sample uploaded" : `${headerType} header`}</span>
          </div>
        ))}

        <div
          className={cn(
            "tw-whitespace-pre-wrap tw-break-words tw-px-2.5 tw-pb-1 tw-pt-2 tw-text-[0.78rem] tw-leading-normal",
            hasBody
              ? "tw-text-[var(--ext-whatsapp-ink)]"
              : "tw-italic tw-text-[var(--ext-whatsapp-muted)]",
          )}
        >
          {hasBody ? body : "No body content yet"}
        </div>

        {footer?.trim() && (
          <div className="tw-px-2.5 tw-pb-1 tw-pt-0.5 tw-text-[0.7rem] tw-text-[var(--ext-whatsapp-muted)]">
            {footer}
          </div>
        )}

        <div className="tw-px-2 tw-pb-1.5 tw-text-right tw-text-[0.65rem] tw-text-[var(--ext-whatsapp-muted)]">
          {new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
        </div>
      </div>

      {buttons.length > 0 && (
        /* The white surface and the bottom corners belong to the stack, not to
           each row — otherwise every button repeats both and the last one needs
           a `:last-child` rule to round itself. */
        <div className="tw-mt-1 tw-overflow-hidden tw-rounded-b-[8px] tw-bg-[var(--ext-whatsapp-bubble-in)]">
          {buttons.slice(0, 3).map((b, i) => (
            <PreviewButton key={`pbtn-${i}`} type={b.type}>
              {b.text || (b.type === "COPY_CODE" ? "Copy offer code" : `Button ${i + 1}`)}
            </PreviewButton>
          ))}
          {buttons.length > 3 && <PreviewButton muted>See all options</PreviewButton>}
        </div>
      )}
    </div>
  );
};

export default TemplatePreview;
